import {
    alpha,
    Box,
    Button,
    Divider,
    IconButton,
    Link,
    Paper,
    Popper,
    Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import {
    KeyboardEvent as ReactKeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import ColorValueFields from '~/components/Inputs/Color/ColorValueFields';
import DicomColorPicker from '~/components/Inputs/Color/DicomColorPicker';
import {
    COLOR_EDIT_OPENER_ATTRIBUTE,
    colorEditWithheldReasonId,
    useStructureColorEdit,
} from '~/hooks/structure-set/useStructureColorEdit';
import useEventListener from '~/hooks/useEventListener';
import { useStructureColorViewerPreview } from '~/hooks/viewer/useStructureColorViewerPreview';
import {
    bulkRoiSelectionActiveState,
    structureColorEditSessionState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import { dicomColorsEqual, dicomColorToHex } from '~/utils/colorUtils';

/** Keeps the popover this far from the window's edges. */
const VIEWPORT_PADDING = 8;

const POPPER_MODIFIERS = [
    { name: 'offset', options: { offset: [0, 10] } },
    {
        name: 'flip',
        options: {
            fallbackPlacements: ['left', 'bottom', 'top'],
            padding: VIEWPORT_PADDING,
        },
    },
    {
        name: 'preventOverflow',
        options: {
            rootBoundary: 'viewport',
            padding: VIEWPORT_PADDING,
            altAxis: true,
        },
    },
];

/** Square, rounded saturation area over a pill-shaped hue slider. */
const PICKER_SX = {
    '& .react-colorful': { width: '100%', height: 'auto' },
    '& .react-colorful__saturation': {
        height: 120,
        flex: 'none',
        borderRadius: 1,
        borderBottom: 'none',
    },
    '& .react-colorful__hue': {
        height: 10,
        flex: 'none',
        mt: 1.5,
        borderRadius: 6,
    },
    // Small enough that a pointer at the far edge stays inside the padding.
    '& .react-colorful__pointer': { width: 16, height: 16 },
    // The value fields at the card's own scale rather than a form's.
    '& .MuiTextField-root .MuiOutlinedInput-root, & .MuiTextField-root .MuiInputBase-input, & .MuiTextField-root .MuiInputLabel-root, & .MuiTextField-root .MuiOutlinedInput-notchedOutline':
        { fontSize: 12 },
    '& .MuiTextField-root .MuiInputBase-input': { py: '5px' },
};

/** The key a button answers to, as a small key cap beside its label. */
function KeyCap({ label }: { label: string }) {
    return (
        <Box
            component="span"
            aria-hidden
            sx={{
                ml: 1,
                px: 0.5,
                fontSize: 9,
                lineHeight: '14px',
                borderRadius: 0.5,
                border: '1px solid currentColor',
                opacity: 0.8,
            }}
        >
            {label}
        </Box>
    );
}

/**
 * The popover in which a structure's display colour is edited, for
 * whichever control opened the edit. One exists; it shows the open edit
 * from {@link structureColorEditSessionState}.
 *
 * Only Enter and Apply save, once, and neither does while a Hex/RGB
 * field holds invalid text. Every other way out — Esc, Cancel, the close
 * button, a press outside — closes it with nothing saved, as does the
 * structure being deleted, its colour edit becoming withheld, another
 * structure set becoming current, or the popover unmounting. Revert returns the colour to the
 * original and leaves the popover open. Enter, Esc and the buttons return
 * focus to the control that opened it. The viewer previews the edit.
 */
export default function StructureColorPopover() {
    const intl = useIntl();
    const session = useRecoilValue(structureColorEditSessionState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    const { setPending, confirm, cancel } = useStructureColorEdit();
    useStructureColorViewerPreview();
    const paperRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [fieldsValid, setFieldsValid] = useState(true);
    // Bumped by Revert: the fields remount, dropping text typed into them.
    const [fieldsEpoch, setFieldsEpoch] = useState(0);

    const roi =
        session && structureSet?.id === session.structureSetId
            ? structureSet.roiArray.find(
                  (r) => r.roiNumber === session.roiNumber
              )
            : undefined;
    const editable =
        !!roi &&
        colorEditWithheldReasonId(roi, isBulkSelectionActive) === undefined;

    useEffect(() => {
        if (session && !editable) {
            cancel();
        }
    }, [session, editable, cancel]);

    const resetSession = useResetRecoilState(structureColorEditSessionState);
    useEffect(() => resetSession, [resetSession]);

    const returnFocusTo = session?.returnFocusTo;
    const closeAndReturnFocus = useCallback(
        (close: () => void) => {
            close();
            returnFocusTo?.focus({ preventScroll: true });
        },
        [returnFocusTo]
    );

    // Capture phase: hosts of the opening controls stop propagation of their
    // own clicks, and a pending contour suggestion takes Esc on the document.
    const handlePressAway = useCallback(
        ({ target }: Event) => {
            // A field's range hint floats outside the popover but belongs
            // to it.
            if (
                target instanceof Element &&
                !paperRef.current?.contains(target) &&
                !target.closest(
                    `[${COLOR_EDIT_OPENER_ATTRIBUTE}], .MuiTooltip-popper`
                )
            ) {
                cancel();
            }
        },
        [cancel]
    );
    const handleEscape = useCallback(
        (event: Event) => {
            if (event instanceof KeyboardEvent && event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeAndReturnFocus(cancel);
            }
        },
        [cancel, closeAndReturnFocus]
    );
    useEventListener('mousedown', handlePressAway, session && document, true);
    useEventListener('touchstart', handlePressAway, session && document, true);
    useEventListener('keydown', handleEscape, session && window, true);

    const focusOnMount = useCallback((node: HTMLDivElement | null) => {
        paperRef.current = node;
        node?.focus({ preventScroll: true });
    }, []);

    if (!session || !roi || !editable) {
        return null;
    }

    const original = session.colorAtOpen;
    const displayColor = session.pending ?? roi.color;
    const changed = !dicomColorsEqual(displayColor, original);
    const revert = () => {
        setPending(null);
        setFieldsEpoch((epoch) => epoch + 1);
        // The link goes with the change: focus stays in the dialog.
        paperRef.current?.focus({ preventScroll: true });
    };

    // Enter applies from anywhere but the card's own buttons — from the
    // picker, a field or a field's stepper. Handled on the way down: a
    // number field keeps its key presses to itself and gives up focus on
    // Enter, which would leave the dialog unable to hear it. While a field
    // is invalid nothing is applied. The key still reaches a field, which
    // keeps its text marked, but is stopped before a stepper, which would
    // step. The fields are asked directly, since their reported validity
    // trails a keystroke by a render.
    const handleEnter = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        const { target, currentTarget } = event;
        const onButton = target instanceof HTMLButtonElement;
        if (
            event.key !== 'Enter' ||
            (onButton && !bodyRef.current?.contains(target))
        ) {
            return;
        }
        if (
            !fieldsValid ||
            currentTarget.querySelector('[aria-invalid="true"]')
        ) {
            if (onButton) {
                event.preventDefault();
            }
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        closeAndReturnFocus(confirm);
    };

    return (
        <Popper
            open
            anchorEl={session.anchor}
            placement={
                session.source === 'properties' ? 'right-start' : 'right'
            }
            modifiers={POPPER_MODIFIERS}
            sx={{ zIndex: 1400 }}
        >
            <Paper
                key={`${session.roiNumber}:${session.source}`}
                ref={focusOnMount}
                role="dialog"
                aria-label={intl.formatMessage(
                    { id: 'colorPicker.dialogLabel' },
                    { structureName: roi.name }
                )}
                tabIndex={-1}
                data-testid="structure-color-popover"
                onKeyDownCapture={handleEnter}
                // A field that gives focus up to nothing (a number field
                // does on Enter) must not drop it out of the dialog, where
                // the next keys would reach the viewer's shortcuts.
                onBlur={({ relatedTarget }) => {
                    if (!relatedTarget && document.hasFocus()) {
                        paperRef.current?.focus({ preventScroll: true });
                    }
                }}
                // The dialog owns its keys: arrows, Delete and letters typed
                // into the fields must not reach the viewer's shortcuts.
                onKeyDown={(event) => event.stopPropagation()}
                // Clicks stay inside: a host panel (Properties) closes on a
                // document click it does not own.
                onClick={(event) => event.stopPropagation()}
                onTouchEnd={(event) => event.stopPropagation()}
                elevation={8}
                sx={{
                    width: 260,
                    maxHeight: `calc(100vh - ${2 * VIEWPORT_PADDING}px)`,
                    overflow: 'hidden auto',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    backgroundImage: 'none',
                    '&:focus': { outline: 'none' },
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    px={1.5}
                    py={1.25}
                >
                    <Box
                        aria-hidden
                        data-testid="structure-color-swatch"
                        display="flex"
                        flexShrink={0}
                        sx={{
                            width: 36,
                            height: 24,
                            borderRadius: 1,
                            overflow: 'hidden',
                        }}
                    >
                        {changed && (
                            <Box
                                flex={1}
                                bgcolor={dicomColorToRgbString(original)}
                                onClick={revert}
                                title={intl.formatMessage({
                                    id: 'colorPicker.revert',
                                })}
                                data-testid="structure-color-original"
                                sx={{ cursor: 'pointer' }}
                            />
                        )}
                        <Box
                            flex={1}
                            bgcolor={dicomColorToRgbString(displayColor)}
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Typography
                            fontSize={13}
                            fontWeight={500}
                            lineHeight={1.3}
                            noWrap
                            title={roi.name}
                        >
                            {roi.name}
                        </Typography>
                        <Typography
                            fontSize={11}
                            color="text.secondary"
                            noWrap
                            data-testid="structure-color-status"
                        >
                            {changed ? (
                                <>
                                    {intl.formatMessage(
                                        { id: 'colorPicker.originalValue' },
                                        { hex: dicomColorToHex(original) }
                                    )}
                                    {' · '}
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onClick={revert}
                                        data-testid="structure-color-revert"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 500,
                                            // A button Link sits at
                                            // 'middle' by default, off the
                                            // text's baseline.
                                            verticalAlign: 'baseline',
                                        }}
                                    >
                                        <FormattedMessage id="colorPicker.revert" />
                                    </Link>
                                </>
                            ) : (
                                <FormattedMessage id="colorPicker.unchanged" />
                            )}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => closeAndReturnFocus(cancel)}
                        aria-label={intl.formatMessage({ id: 'close' })}
                        sx={{ p: 0.25, alignSelf: 'flex-start' }}
                    >
                        <X size={16} />
                    </IconButton>
                </Box>
                <Divider />
                <Box
                    ref={bodyRef}
                    p={1.5}
                    display="grid"
                    gap={1.5}
                    sx={PICKER_SX}
                >
                    <DicomColorPicker
                        color={displayColor}
                        onChange={setPending}
                    />
                    <ColorValueFields
                        key={fieldsEpoch}
                        color={displayColor}
                        onChange={setPending}
                        onValidityChange={setFieldsValid}
                    />
                </Box>
                <Divider />
                <Box display="flex" gap={1} px={1.5} py={1.25}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={() => closeAndReturnFocus(cancel)}
                        data-testid="structure-color-cancel"
                        sx={{ flex: 1, borderColor: 'divider' }}
                    >
                        <FormattedMessage id="cancel" />
                        <KeyCap label="Esc" />
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => closeAndReturnFocus(confirm)}
                        disabled={!changed || !fieldsValid}
                        data-testid="structure-color-apply"
                        sx={(theme) => ({
                            flex: 1,
                            '&.Mui-disabled': {
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.35
                                ),
                                color: alpha(theme.palette.common.white, 0.5),
                            },
                        })}
                    >
                        <FormattedMessage id="colorPicker.apply" />
                        <KeyCap label="Enter" />
                    </Button>
                </Box>
            </Paper>
        </Popper>
    );
}
